export const TIMELINE_VIEWPORT_SYNC_MODE_ABSOLUTE = "absolute";
export const TIMELINE_VIEWPORT_SYNC_MODE_RELATIVE = "relative";

export function parseTimelineViewportDate(value) {
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function normalizeTimelineViewportRange(range) {
    const start = parseTimelineViewportDate(range?.start);
    const end = parseTimelineViewportDate(range?.end);

    if (!start || !end || end.getTime() <= start.getTime()) {
        return null;
    }

    return {
        start,
        end,
        durationMs: end.getTime() - start.getTime(),
    };
}

function getViewportAnchor(meta, fallbackRange) {
    return parseTimelineViewportDate(
        meta?.segmentAnchor
            ?? meta?.anchor
            ?? meta?.domainStart
            ?? meta?.timeWindow?.start,
    ) || fallbackRange?.start || null;
}

export function shouldSyncTimelineViewportRelatively(sourceMeta, targetMeta) {
    return sourceMeta?.syncMode === TIMELINE_VIEWPORT_SYNC_MODE_RELATIVE
        || targetMeta?.syncMode === TIMELINE_VIEWPORT_SYNC_MODE_RELATIVE;
}

export function buildBoundTimelineViewportRange(sourceRange, sourceMeta = {}, targetMeta = {}) {
    const normalizedSourceRange = normalizeTimelineViewportRange(sourceRange);
    if (!normalizedSourceRange) {
        return null;
    }

    if (shouldSyncTimelineViewportRelatively(sourceMeta, targetMeta)) {
        const sourceAnchor = getViewportAnchor(sourceMeta, normalizedSourceRange);
        const targetAnchor = getViewportAnchor(targetMeta, normalizedSourceRange);
        if (!sourceAnchor || !targetAnchor) {
            return null;
        }

        const offsetMs = normalizedSourceRange.start.getTime() - sourceAnchor.getTime();
        const targetStart = new Date(targetAnchor.getTime() + offsetMs);
        return {
            start: targetStart,
            end: new Date(targetStart.getTime() + normalizedSourceRange.durationMs),
        };
    }

    return {
        start: new Date(normalizedSourceRange.start.getTime()),
        end: new Date(normalizedSourceRange.end.getTime()),
    };
}

export function areTimelineViewportRangesEqual(left, right, toleranceMs = 1) {
    const normalizedLeft = normalizeTimelineViewportRange(left);
    const normalizedRight = normalizeTimelineViewportRange(right);
    if (!normalizedLeft || !normalizedRight) {
        return normalizedLeft === normalizedRight;
    }

    return Math.abs(normalizedLeft.start.getTime() - normalizedRight.start.getTime()) <= toleranceMs
        && Math.abs(normalizedLeft.end.getTime() - normalizedRight.end.getTime()) <= toleranceMs;
}
